import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import dayjs from 'dayjs';
import { ICustomer } from '../models/customer.model';
import { ResourceUtil } from '../app/shared/util/resource-util';
import { CounterStatsModel } from '../models/counter-stats-model';
import { AccountService } from './account.service';
import { BaryChart } from '../models/bary-chart.model';
import { StatsPeriod } from '../models/stats-period.model';

@Injectable({ providedIn: 'root' })
export class StatisticsService {
  constructor(protected http: HttpClient, private accountService: AccountService) {}

  public getCountersList(customer: ICustomer, table: string, id: number, billDate: boolean = false): Observable<HttpResponse<string[]>> {
    if (this.accountService.hasAnyAuthority('ROLE_MODERATOR')) {
      return this.http.get<string[]>(ResourceUtil.asModeratorURL('/stats', customer) + '/counters-list/' + table + '/' + id + `?billDate=${billDate}`, { observe: 'response' });
    } else {
      return this.http.get<string[]>(ResourceUtil.asUserURL('/stats', customer) + '/counters-list/' + table + '/' + id + `?billDate=${billDate}`, { observe: 'response' });
    }
  }

  public getCounters(customer: ICustomer, table: string, id: number, counters: string[], billDate: boolean = false): Observable<HttpResponse<CounterStatsModel[]>> {
    if (this.accountService.hasAnyAuthority('ROLE_MODERATOR')) {
      return this.http.post<CounterStatsModel[]>(ResourceUtil.asModeratorURL('/stats', customer) + '/counters-stats/' + table + '/' + id + `?billDate=${billDate}`, counters, {
        observe: 'response',
      });
    } else {
      return this.http.post<CounterStatsModel[]>(ResourceUtil.asUserURL('/stats', customer) + '/counters-stats/' + table + '/' + id, counters, { observe: 'response' });
    }
  }

  getAveragesList(customer: ICustomer): Observable<HttpResponse<string[]>> {
    if (this.accountService.hasAnyAuthority('ROLE_MODERATOR')) {
      return this.http.get<string[]>(ResourceUtil.asModeratorURL('/stats', customer) + '/average-list', { observe: 'response' });
    } else {
      return this.http.get<string[]>(ResourceUtil.asUserURL('/stats', customer) + '/average-list', { observe: 'response' });
    }
  }

  getAverages(customer: ICustomer, id: number, averages: string[], table: string): Observable<HttpResponse<CounterStatsModel[]>> {
    if (this.accountService.hasAnyAuthority('ROLE_MODERATOR')) {
      return this.http.post<CounterStatsModel[]>(ResourceUtil.asModeratorURL('/stats', customer) + `/average-stats/${table}/${id}`, averages, { observe: 'response' });
    } else {
      return this.http.post<CounterStatsModel[]>(ResourceUtil.asUserURL('/stats', customer) + `/average-stats/${table}/${id}`, averages, { observe: 'response' });
    }
  }

  getChartDtoStatsPeriod(customer: ICustomer, query: string, table: string, period: StatsPeriod, id: number, billDate: boolean = false): Observable<HttpResponse<BaryChart[]>> {
    if (this.accountService.hasAnyAuthority('ROLE_MODERATOR')) {
      return this.http.get<BaryChart[]>(
        ResourceUtil.asModeratorURL('/stats/' + query + '?aimedTable=' + table + '&period=' + period + '&id=' + id + '&billDate=' + billDate, customer),
        {
          observe: 'response',
        }
      );
    } else {
      return this.http.get<BaryChart[]>(ResourceUtil.asUserURL('/stats/' + query + '?aimedTable=' + table + '&period=' + period + '&id=' + id, customer), { observe: 'response' });
    }
  }

  getChartDtoStats(
    customer: ICustomer,
    query: string,
    table: string,
    startDate: Date,
    endDate: Date,
    id: number,
    billDate: boolean = false
  ): Observable<HttpResponse<BaryChart[]>> {
    const formattedStartDate: string = this.getFormattedDate(startDate);
    const formattedEndDate: string = this.getFormattedDate(endDate);
    const url: string = '/stats/' + query + '?aimedTable=' + table + '&startDate=' + formattedStartDate + '&endDate=' + formattedEndDate + '&id=' + id;
    if (this.accountService.hasAnyAuthority('ROLE_MODERATOR')) {
      return this.http.get<BaryChart[]>(ResourceUtil.asModeratorURL(url + '&billDate=' + billDate, customer), { observe: 'response' });
    } else {
      return this.http.get<BaryChart[]>(ResourceUtil.asUserURL(url, customer), { observe: 'response' });
    }
  }

  private getFormattedDate(date: Date): string {
    return dayjs(date).format('DD.MM.YYYY');
  }
}
